<template>
  <van-dialog
    v-model="visible"
    title="搜索公司地址"
    show-cancel-button
    @confirm="handleConfirm"
    @cancel="handleCancel"
    class="address-search-modal"
  >
    <div class="search-container">
      <!-- 搜索输入框 -->
      <div class="search-input-wrapper">
        <van-field
          v-model="searchKeyword"
          placeholder="请输入公司名称、地标建筑或详细地址"
          clearable
          @input="onSearchInput"
          @keyup.enter="handleSearch"
        >
          <template #button>
            <van-button size="small" type="primary" @click="handleSearch" :loading="searching">
              搜索
            </van-button>
          </template>
        </van-field>
      </div>

      <!-- 搜索结果列表 -->
      <div class="results-container" v-if="searchResults.length > 0">
        <div class="results-header">搜索结果</div>
        <div class="results-list">
          <div
            v-for="(item, index) in searchResults"
            :key="index"
            class="result-item"
            :class="{ selected: selectedIndex === index }"
            @click="selectAddress(index)"
          >
            <div class="address-name">{{ item.name }}</div>
            <div class="address-detail">{{ item.address }}</div>
            <div class="address-location">
              经度: {{ item.location.split(',')[0] }} | 纬度: {{ item.location.split(',')[1] }}
            </div>
            <div class="address-district">
              {{ item.pname }} {{ item.cityname }} {{ item.adname }}
            </div>
          </div>
        </div>
      </div>

      <!-- 无结果提示 -->
      <div class="no-results" v-if="searchKeyword && !searching && searchResults.length === 0">
        <van-empty description="未找到相关地址信息" />
      </div>

      <!-- 搜索提示 -->
      <div class="search-tip" v-if="!searchKeyword && searchResults.length === 0">
        <van-empty description="输入公司名称、地标建筑或详细地址进行搜索" />
      </div>
    </div>
  </van-dialog>
</template>

<script>
import axios from 'axios'
import { Toast } from 'vant'
import { API_ENDPOINTS } from '@/config/api'

export default {
  name: 'AddressSearchModal',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      visible: false,
      searchKeyword: '',
      searchResults: [],
      selectedIndex: -1,
      searching: false,
      searchTimer: null
    }
  },
  watch: {
    value(newVal) {
      this.visible = newVal
      if (newVal) {
        // 弹窗打开时重置状态
        this.resetState()
      }
    },
    visible(newVal) {
      if (!newVal) {
        this.$emit('input', false)
      }
    }
  },
  methods: {
    // 重置状态
    resetState() {
      this.searchKeyword = ''
      this.searchResults = []
      this.selectedIndex = -1
      this.searching = false
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
        this.searchTimer = null
      }
    },

    // 搜索输入防抖处理
    onSearchInput(value) {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }
      
      if (!value.trim()) {
        this.searchResults = []
        this.selectedIndex = -1
        return
      }

      // 防抖搜索，500ms后自动搜索
      this.searchTimer = setTimeout(() => {
        this.handleSearch()
      }, 500)
    },

    // 执行搜索
    async handleSearch() {
      if (!this.searchKeyword.trim()) {
        Toast('请输入搜索关键词')
        return
      }

      if (this.searching) {
        return
      }

      try {
        this.searching = true
        
        const token = localStorage.getItem('token')
        if (!token) {
          Toast('请先登录')
          return
        }

        const response = await axios.post(API_ENDPOINTS.GXY.SEARCH_ADDR, {
          address: this.searchKeyword.trim()
        }, {
          timeout: 10000,
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (response.data.code === 0) {
          this.searchResults = response.data.data || []
          this.selectedIndex = -1
          
          if (this.searchResults.length === 0) {
            Toast('未找到相关地址信息')
          }
        } else {
          Toast(response.data.message || '搜索失败')
          this.searchResults = []
        }
      } catch (error) {
        console.error('地址搜索失败:', error)
        Toast('搜索失败，请检查网络连接')
        this.searchResults = []
      } finally {
        this.searching = false
      }
    },

    // 选择地址
    selectAddress(index) {
      this.selectedIndex = index
    },

    // 确认选择
    handleConfirm() {
      if (this.selectedIndex >= 0 && this.selectedIndex < this.searchResults.length) {
        const selectedAddress = this.searchResults[this.selectedIndex]
        
        // 解析经纬度
        const [longitude, latitude] = selectedAddress.location.split(',')
        
        // 构造返回的地址信息
        const addressInfo = {
          name: selectedAddress.name,
          address: selectedAddress.address,
          province: selectedAddress.pname,
          city: selectedAddress.cityname,
          district: selectedAddress.adname,
          longitude: longitude,
          latitude: latitude,
          fullAddress: `${selectedAddress.pname}${selectedAddress.cityname}${selectedAddress.adname}${selectedAddress.address}`
        }
        
        this.$emit('select', addressInfo)
        this.visible = false
      } else {
        Toast('请先选择一个地址')
      }
    },

    // 取消选择
    handleCancel() {
      this.visible = false
    }
  }
}
</script>

<style scoped>
.address-search-modal {
  --van-dialog-width: 90vw;
  --van-dialog-max-height: 80vh;
}

.search-container {
  padding: 16px 0;
  max-height: 60vh;
  display: flex;
  flex-direction: column;
}

.search-input-wrapper {
  margin-bottom: 16px;
  flex-shrink: 0;
}

.results-container {
  flex: 1;
  overflow-y: auto;
}

.results-header {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.results-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.result-item {
  padding: 12px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #fff;
}

.result-item:hover {
  border-color: #00CB6A;
  box-shadow: 0 2px 8px rgba(0, 203, 106, 0.1);
}

.result-item.selected {
  border-color: #00CB6A;
  background: rgba(0, 203, 106, 0.05);
  box-shadow: 0 2px 8px rgba(0, 203, 106, 0.2);
}

.address-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.address-detail {
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
  line-height: 1.4;
}

.address-location {
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
}

.address-district {
  font-size: 12px;
  color: #00CB6A;
  font-weight: 500;
}

.no-results,
.search-tip {
  padding: 40px 0;
  text-align: center;
}

/* 搜索按钮样式 */
.search-input-wrapper :deep(.van-field__button) {
  margin-left: 8px;
}

.search-input-wrapper :deep(.van-button--small) {
  height: 32px;
  padding: 0 12px;
  font-size: 14px;
}

/* 滚动条样式 */
.results-container::-webkit-scrollbar {
  width: 4px;
}

.results-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.results-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.results-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 响应式设计 */
@media (max-width: 480px) {
  .address-search-modal {
    --van-dialog-width: 95vw;
  }
  
  .search-container {
    max-height: 70vh;
  }
}
</style>